import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Users,
    UserCheck,
    UserPlus,
    ShieldCheck,
    Search,
    Mail,
    Phone,
    Calendar,
    Building2,
    RefreshCw,
    AlertTriangle,
} from "lucide-react";
import { getVendors } from "../service/userService";

/* =========================================================================
 * HELPERS
 * ======================================================================= */
function formatDate(isoString) {
    if (!isoString) return "-";
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "-";
    const day = d.getDate();
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = d.getFullYear();
    return { day, month, year };
}

function formatRole(role) {
    if (!role) return "VENDOR";
    return role.replace(/^ROLE_/, "");
}

function initialsOf(name) {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

/* =========================================================================
 * SMALL UI PIECES
 * ======================================================================= */
function StatCard({ icon: Icon, value, label, iconBg, iconColor }) {
    return (
        <div className="stat-card">
            <div className="stat-icon" style={{ background: iconBg, color: iconColor }}>
                <Icon size={20} strokeWidth={2.2} />
            </div>
            <div>
                <div className="stat-value">{value}</div>
                <div className="stat-label">{label}</div>
            </div>
        </div>
    );
}

function StatusPill({ active }) {
    return (
        <span className={`status-pill ${active ? "status-active" : "status-suspended"}`}>
            {active ? "Active" : "Suspended"}
        </span>
    );
}

function Avatar({ name }) {
    return <div className="avatar">{initialsOf(name)}</div>;
}

function DateCell({ isoString }) {
    const parts = formatDate(isoString);
    if (parts === "-") {
        return (
            <div className="date-cell muted">
                <Calendar size={14} />
                <span>-</span>
            </div>
        );
    }
    return (
        <div className="date-cell">
            <Calendar size={14} />
            <span>
                {parts.day} {parts.month} {parts.year}
            </span>
        </div>
    );
}

/* =========================================================================
 * MAIN SCREEN
 * ======================================================================= */
export default function VendorsScreen() {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("ALL");
    const [statusFilter, setStatusFilter] = useState("ALL");

    // Single source of truth for loading vendors — calls the real
    // apiservice/userService.getVendors() function.
    const loadVendors = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getVendors();

            if (response.success) {
                setVendors(Array.isArray(response.data) ? response.data : []);
            } else {
                setError(response.message || "Failed to fetch vendors.");
            }
        } catch (err) {
            console.error("Error fetching vendors:", err);
            setError(err.message || "Something went wrong while loading vendors.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadVendors();
    }, [loadVendors]);

    const roles = useMemo(() => {
        const set = new Set(vendors.map((v) => formatRole(v.role)));
        return ["ALL", ...Array.from(set)];
    }, [vendors]);

    const filteredVendors = useMemo(() => {
        return vendors.filter((v) => {
            const matchesSearch =
                !search ||
                [v.name, v.businessName, v.email, v.mobileNumber]
                    .filter(Boolean)
                    .some((field) => field.toLowerCase().includes(search.toLowerCase()));

            const matchesRole = roleFilter === "ALL" || formatRole(v.role) === roleFilter;

            const matchesStatus =
                statusFilter === "ALL" ||
                (statusFilter === "ACTIVE" && v.enabled) ||
                (statusFilter === "SUSPENDED" && !v.enabled);

            return matchesSearch && matchesRole && matchesStatus;
        });
    }, [vendors, search, roleFilter, statusFilter]);

    const totalVendors = vendors.length;
    const activeVendors = vendors.filter((v) => v.enabled).length;
    const suspendedVendors = vendors.filter((v) => !v.enabled).length;
    const pendingApproval = 0; // API does not currently expose a pending-approval state

    return (
        <div className="screen">
            <style>{`
        .screen {
          --bg: #f5f6fa;
          --card: #ffffff;
          --border: #e8e9ee;
          --text: #14161f;
          --muted: #7a7d8c;
          --accent: #5b5fed;
          background: var(--bg);
          min-height: 100vh;
          padding: 28px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, sans-serif;
          color: var(--text);
        }
        * { box-sizing: border-box; }

        .header-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 22px;
        }
        .title { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; margin: 0; }
        .subtitle { color: var(--muted); font-size: 14px; margin-top: 4px; }
        .refresh-btn {
          display: flex; align-items: center; gap: 6px;
          background: var(--card); border: 1px solid var(--border);
          border-radius: 10px; padding: 9px 14px; font-size: 13px; font-weight: 600;
          color: var(--text); cursor: pointer;
        }
        .refresh-btn:hover { background: #fafafe; }
        .refresh-btn:disabled { opacity: 0.6; cursor: default; }
        .refresh-btn svg.spinning { animation: spin 0.9s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 22px;
        }
        @media (max-width: 900px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } }

        .stat-card {
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 18px;
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .stat-icon {
          width: 44px; height: 44px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .stat-value { font-size: 24px; font-weight: 800; line-height: 1.1; }
        .stat-label { color: var(--muted); font-size: 13px; margin-top: 2px; }

        .panel {
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: 16px;
          overflow: hidden;
        }
        .panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          padding: 20px 22px;
          border-bottom: 1px solid var(--border);
        }
        .panel-title { font-size: 17px; font-weight: 800; margin: 0; }
        .panel-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }

        .controls { display: flex; gap: 10px; flex-wrap: wrap; }
        .search-box {
          display: flex; align-items: center; gap: 8px;
          border: 1px solid var(--border); border-radius: 10px;
          padding: 9px 12px; min-width: 260px; background: var(--bg);
        }
        .search-box input {
          border: none; outline: none; background: transparent; font-size: 13.5px;
          width: 100%; color: var(--text);
        }
        .search-box svg { color: var(--muted); flex-shrink: 0; }

        select.filter-select {
          border: 1px solid var(--border); border-radius: 10px;
          padding: 9px 12px; font-size: 13.5px; font-weight: 600;
          background: var(--card) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%237a7d8c'/%3E%3C/svg%3E") no-repeat right 12px center;
          appearance: none; -webkit-appearance: none; color: var(--text); cursor: pointer;
          padding-right: 28px;
        }

        table { width: 100%; border-collapse: collapse; }
        thead th {
          text-align: left; font-size: 11.5px; letter-spacing: 0.06em;
          color: var(--muted); text-transform: uppercase; font-weight: 700;
          padding: 12px 22px; background: #fafafc; border-bottom: 1px solid var(--border);
        }
        tbody td {
          padding: 16px 22px; border-bottom: 1px solid var(--border); font-size: 14px;
          vertical-align: middle;
        }
        tbody tr:last-child td { border-bottom: none; }
        tbody tr:hover { background: #fafbff; }

        .user-cell { display: flex; align-items: center; gap: 12px; }
        .avatar {
          width: 38px; height: 38px; border-radius: 50%;
          background: #eceafd; color: #5b5fed; font-weight: 700; font-size: 13px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .user-name { font-weight: 700; }
        .user-sub { color: var(--muted); font-size: 12.5px; display: flex; align-items: center; gap: 5px; margin-top: 2px; }

        .icon-cell { display: flex; align-items: center; gap: 7px; color: var(--text); }
        .icon-cell svg { color: var(--muted); flex-shrink: 0; }

        .role-badge {
          border: 1px solid var(--border); border-radius: 999px;
          padding: 4px 12px; font-size: 11.5px; font-weight: 700;
          letter-spacing: 0.03em; color: var(--text); display: inline-block;
        }

        .date-cell { display: flex; align-items: center; gap: 7px; color: var(--text); font-size: 13.5px; }
        .date-cell svg { color: var(--muted); }
        .date-cell.muted { color: var(--muted); }

        .status-pill {
          padding: 5px 13px; border-radius: 999px; font-size: 12px; font-weight: 700; display: inline-block;
        }
        .status-active { background: #e5f8ec; color: #1c9a4b; }
        .status-suspended { background: #fdeaea; color: #d5423f; }

        .empty-state, .loading-state, .error-state {
          padding: 60px 24px; text-align: center; color: var(--muted); font-size: 14px;
        }
        .error-state { color: #d5423f; display: flex; flex-direction: column; align-items: center; gap: 10px; }
        .error-state button {
          margin-top: 4px; background: var(--accent); color: white; border: none;
          padding: 8px 16px; border-radius: 8px; font-weight: 700; font-size: 13px; cursor: pointer;
        }
      `}</style>

            <div className="header-row">
                <div>
                    <h1 className="title">Vendors</h1>
                    <p className="subtitle">View and manage everyone registered across all vendors</p>
                </div>
                <button className="refresh-btn" onClick={loadVendors} disabled={loading}>
                    <RefreshCw size={14} className={loading ? "spinning" : ""} />
                    Refresh
                </button>
            </div>

            <div className="stats-grid">
                <StatCard icon={Users} value={totalVendors} label="Total vendors" iconBg="#eceafd" iconColor="#5b5fed" />
                <StatCard icon={UserCheck} value={activeVendors} label="Active" iconBg="#e5f8ec" iconColor="#1c9a4b" />
                <StatCard icon={UserPlus} value={pendingApproval} label="Pending approval" iconBg="#fff4e0" iconColor="#d99a1f" />
                <StatCard icon={ShieldCheck} value={suspendedVendors} label="Suspended" iconBg="#fdeaea" iconColor="#d5423f" />
            </div>

            <div className="panel">
                <div className="panel-header">
                    <div>
                        <h2 className="panel-title">Registered vendors</h2>
                        <p className="panel-sub">
                            {filteredVendors.length} of {totalVendors} vendors
                        </p>
                    </div>
                    <div className="controls">
                        <div className="search-box">
                            <Search size={15} />
                            <input
                                placeholder="Search name, email, phone..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <select className="filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                            {roles.map((r) => (
                                <option key={r} value={r}>
                                    {r === "ALL" ? "All Roles" : r}
                                </option>
                            ))}
                        </select>
                        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="ALL">All Status</option>
                            <option value="ACTIVE">Active</option>
                            <option value="SUSPENDED">Suspended</option>
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div className="loading-state">Loading vendors…</div>
                ) : error ? (
                    <div className="error-state">
                        <AlertTriangle size={22} />
                        <div>{error}</div>
                        <button onClick={loadVendors}>Try again</button>
                    </div>
                ) : filteredVendors.length === 0 ? (
                    <div className="empty-state">No vendors match your filters.</div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Vendor</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Role</th>
                                <th>Registered on</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVendors.map((v) => (
                                <tr key={v.id}>
                                    <td>
                                        <div className="user-cell">
                                            <Avatar name={v.name} />
                                            <div>
                                                <div className="user-name">{v.name}</div>
                                                <div className="user-sub">
                                                    <Building2 size={12} />
                                                    {v.businessName || "-"}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="icon-cell">
                                            <Mail size={14} />
                                            {v.email}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="icon-cell">
                                            <Phone size={14} />
                                            {v.mobileNumber}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="role-badge">{formatRole(v.role)}</span>
                                    </td>
                                    <td>
                                        <DateCell isoString={v.createdAt} />
                                    </td>
                                    <td>
                                        <StatusPill active={v.enabled} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}